import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ContentPagesStore } from './content-pages.store';
import { ContentPagesQuery } from './content-pages.query';
import {
  ContentPage,
  CreateContentPageDto,
  UpdateContentPageDto,
  ContentPageQueryParams,
  BulkDeleteDto,
} from './content-page.model';
import { Observable, throwError } from 'rxjs';
import { tap, catchError, finalize } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

interface PaginatedResponse {
  items: ContentPage[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

@Injectable({ providedIn: 'root' })
export class ContentPagesService {
  private apiUrl = `${environment.apiUrl}/content-pages`;

  constructor(
    private http: HttpClient,
    private store: ContentPagesStore,
    private query: ContentPagesQuery
  ) {}

  getAll(params?: ContentPageQueryParams): Observable<PaginatedResponse> {
    this.store.setLoading(true);

    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(key => {
        const value = params[key as keyof ContentPageQueryParams];
        if (value !== undefined && value !== null) {
          httpParams = httpParams.set(key, value.toString());
        }
      });
    }

    return this.http.get<PaginatedResponse>(this.apiUrl, { params: httpParams }).pipe(
      tap(response => {
        this.store.set(response.items);
        this.store.updatePagination({
          page: response.page,
          pageSize: response.pageSize,
          total: response.total,
          totalPages: response.totalPages,
        });
      }),
      catchError(error => {
        this.store.setError(error);
        return throwError(error);
      }),
      finalize(() => this.store.setLoading(false))
    );
  }

  getById(id: string): Observable<ContentPage> {
    this.store.setLoading(true);

    return this.http.get<ContentPage>(`${this.apiUrl}/${id}`).pipe(
      tap(page => {
        this.store.upsert(id, page);
      }),
      catchError(error => {
        this.store.setError(error);
        return throwError(error);
      }),
      finalize(() => this.store.setLoading(false))
    );
  }

  getBySlug(slug: string): Observable<ContentPage> {
    this.store.setLoading(true);

    return this.http.get<ContentPage>(`${this.apiUrl}/by-slug/${slug}`).pipe(
      tap(page => {
        this.store.upsert(page.id, page);
      }),
      catchError(error => {
        this.store.setError(error);
        return throwError(error);
      }),
      finalize(() => this.store.setLoading(false))
    );
  }

  create(dto: CreateContentPageDto): Observable<ContentPage> {
    this.store.setLoading(true);

    return this.http.post<ContentPage>(this.apiUrl, dto).pipe(
      tap(page => {
        this.store.add(page);
        // Refresh the list to update pagination
        this.refreshList();
      }),
      catchError(error => {
        this.store.setError(error);
        return throwError(error);
      }),
      finalize(() => this.store.setLoading(false))
    );
  }

  update(id: string, dto: UpdateContentPageDto): Observable<ContentPage> {
    this.store.setLoading(true);

    return this.http.patch<ContentPage>(`${this.apiUrl}/${id}`, dto).pipe(
      tap(page => {
        this.store.update(id, page);
      }),
      catchError(error => {
        this.store.setError(error);
        return throwError(error);
      }),
      finalize(() => this.store.setLoading(false))
    );
  }

  delete(id: string, cascadeDeleteSections = false): Observable<any> {
    this.store.setLoading(true);

    const params = new HttpParams().set('cascadeDeleteSections', cascadeDeleteSections.toString());

    return this.http.delete(`${this.apiUrl}/${id}`, { params }).pipe(
      tap(() => {
        this.store.remove(id);
        // Refresh the list to update pagination
        this.refreshList();
      }),
      catchError(error => {
        this.store.setError(error);
        return throwError(error);
      }),
      finalize(() => this.store.setLoading(false))
    );
  }

  bulkDelete(dto: BulkDeleteDto): Observable<any> {
    this.store.setLoading(true);

    return this.http.request('delete', this.apiUrl, { body: dto }).pipe(
      tap(() => {
        this.store.remove(dto.ids);
        // Refresh the list to update pagination
        this.refreshList();
      }),
      catchError(error => {
        this.store.setError(error);
        return throwError(error);
      }),
      finalize(() => this.store.setLoading(false))
    );
  }

  resolveByPath(path: string): Observable<ContentPage> {
    const params = new HttpParams().set('path', path);
    const headers = {
      'X-Site-Id': environment.organizationId  // Use organizationId from environment (site UUID)
    };

    return this.http.get<ContentPage>(`${this.apiUrl}/resolve`, { params, headers }).pipe(
      catchError(error => {
        if (error.status === 404) {
          // Page not found is expected for non-existent paths
          return throwError(error);
        }
        return throwError(error);
      })
    );
  }

  search(query: string) {
    this.store.setSearchQuery(query);
    this.refreshList();
  }

  setFilterPublished(isPublished?: boolean) {
    this.store.setFilterPublished(isPublished);
    this.refreshList();
  }

  setSort(field: string, order: 'asc' | 'desc') {
    this.store.setSort(field, order);
    this.refreshList();
  }

  setPage(page: number) {
    this.store.updatePagination({ page });
    this.refreshList();
  }

  setPageSize(pageSize: number) {
    this.store.updatePagination({ pageSize });
    this.refreshList();
  }

  refreshList() {
    const params = this.query.currentQueryParams;
    this.getAll(params).subscribe();
  }

  clearError() {
    this.store.setError(undefined);
  }

  reset() {
    this.store.reset();
  }

  // Utility method to generate slug from title
  generateSlug(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // Validate slug format
  isValidSlug(slug: string): boolean {
    return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);
  }
}
